import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CalendarDays, Flame, Heart, ShieldCheck, Users } from "lucide-react";
import { SiteShell } from "@/project/components/site-shell";
import { Badge } from "@/project/components/cards";
import { formatINR } from "@/project/data/sample";
import { getCampaign, getPartner, campaignPct, daysLeft, domainName, platformFee } from "@/project/data/network";

export const Route = createFileRoute("/campaigns/$id")({
  loader: ({ params }) => {
    const campaign = getCampaign(params.id);
    if (!campaign) throw notFound();
    return { campaign };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Campaign unavailable — Daan Amrut" }, { name: "robots", content: "noindex" }] };
    const c = loaderData.campaign;
    return {
      meta: [
        { title: `${c.title} — Daan Amrut` },
        { name: "description", content: c.summary.slice(0, 155) },
        { property: "og:title", content: `${c.title} — Daan Amrut` },
        { property: "og:description", content: c.summary.slice(0, 155) },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  errorComponent: () => <Missing />,
  notFoundComponent: () => <Missing />,
  component: CampaignDetail,
});

function Missing() {
  return (
    <SiteShell>
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Campaign unavailable</h1>
        <Link to="/campaigns" className="mt-6 inline-block rounded-full bg-[var(--maroon)] px-5 py-2.5 text-sm font-semibold text-[var(--cream)]">Browse campaigns</Link>
      </div>
    </SiteShell>
  );
}

function CampaignDetail() {
  const { id } = Route.useParams();
  const c = getCampaign(id)!;
  const org = getPartner(c.orgId);
  const pct = campaignPct(c);
  const total = c.budget.reduce((s, b) => s + b.amount, 0);

  return (
    <SiteShell>
      <div className="mx-auto grid max-w-7xl gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[1fr_360px]">
        <article className="space-y-8">
          <div>
            <div className="flex flex-wrap gap-2">
              <Badge tone="neutral">{domainName(c.domain)}</Badge>
              {c.urgency !== "Normal" && <Badge tone={c.urgency === "Critical" ? "critical" : "urgent"}><Flame className="h-3 w-3" /> {c.urgency}</Badge>}
              <Badge tone="featured">{c.status}</Badge>
            </div>
            <h1 className="mt-3 font-display text-2xl font-extrabold leading-tight sm:text-4xl">{c.title}</h1>
            {org && (
              <Link to="/organisations/$id" params={{ id: org.id }} className="mt-2 inline-flex items-center gap-2 text-sm text-foreground/70 hover:text-[var(--maroon)]">
                <ShieldCheck className="h-4 w-4 text-[var(--success)]" /> By {org.name}, {org.city}
              </Link>
            )}
          </div>

          <img src={c.image} alt={c.title} className="aspect-[16/9] w-full rounded-2xl object-cover" />

          <section>
            <h2 className="font-display text-xl font-bold">About this campaign</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground/75">{c.story}</p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/75">{c.summary}</p>
          </section>

          <section>
            <h2 className="font-display text-xl font-bold">Where your money goes</h2>
            <ul className="mt-4 space-y-3">
              {c.budget.map((b) => {
                const share = Math.round((b.amount / total) * 100);
                return (
                  <li key={b.head}>
                    <div className="flex justify-between text-sm"><span>{b.head}</span><span className="font-semibold">{formatINR(b.amount)} · {share}%</span></div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-[var(--cream-2)]">
                      <div className="h-full" style={{ width: `${share}%`, background: "var(--gradient-warm)" }} />
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-foreground/60">{platformFee.note}</p>
          </section>

          <section className="rounded-2xl border border-border/60 bg-[var(--cream-2)] p-6">
            <h2 className="font-display text-lg font-bold">Safeguards on this campaign</h2>
            <ul className="mt-3 grid gap-2 text-sm text-foreground/75 sm:grid-cols-2">
              <li>· Organisation KYC, 12A and 80G verified by the foundation</li>
              <li>· Funds settle only to a verified, name-matched bank account</li>
              <li>· Expense proof required before the next payout is released</li>
              <li>· Instant 80G receipt issued by Daan Amrut Foundation</li>
            </ul>
          </section>
        </article>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-[var(--shadow-card)]">
            <p className="font-display text-2xl font-extrabold text-[var(--maroon)]">{formatINR(c.raised)}</p>
            <p className="text-xs text-foreground/60">raised of {formatINR(c.goal)} goal</p>
            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[var(--cream-2)]">
              <div className="h-full" style={{ width: `${pct}%`, background: "var(--gradient-warm)" }} />
            </div>
            <div className="mt-3 flex justify-between text-xs text-foreground/65">
              <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {c.donors} donors</span>
              <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {daysLeft(c.endDate)} days left</span>
            </div>
            <Link
              to="/donate"
              search={{ campaign: c.id, cause: c.causeId, org: org?.id, institution: org?.institutionId }}
              className="mt-5 block rounded-full bg-[var(--maroon)] px-5 py-3 text-center text-sm font-semibold text-[var(--cream)]"
            >
              Donate to this campaign
            </Link>
            {org && (
              <Link to="/organisations/$id" params={{ id: org.id }} className="mt-2 block rounded-full border border-border px-5 py-3 text-center text-sm font-semibold">
                View organisation
              </Link>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile Sticky Bottom Action Bar */}
      <div className="lg:hidden fixed bottom-14 left-0 right-0 z-40 border-t border-border bg-background/95 backdrop-blur-md p-3 px-4 shadow-[0_-4px_16px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between gap-3 max-w-md mx-auto">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Raised {formatINR(c.raised)}</p>
            <p className="text-xs font-bold text-[var(--maroon-deep)]">{pct}% of {formatINR(c.goal)}</p>
          </div>
          <Link
            to="/donate"
            search={{ campaign: c.id, cause: c.causeId, org: org?.id, institution: org?.institutionId }}
            className="flex items-center gap-1.5 rounded-full bg-[var(--maroon)] px-5 py-2.5 text-xs font-bold text-white shadow-md active:scale-95 transition"
          >
            <Heart className="h-4 w-4 fill-white" /> Donate Now
          </Link>
        </div>
      </div>
    </SiteShell>
  );
}
